/**
 *
 * PortalDrawer
 *
 * @version 1.0.0
 * @author giuseppe.paternostro
 * @since 22-Feb-2023
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import style from './PortalDrawer.scss';
import main from '../../styles/scss/main.scss';
import formattedMessage from './languageModule';
// navigation
import { useNavigate } from 'react-router-dom';
// import component
import PortalAnchorButton from '../PortalAnchorButton';
import PortalButton from '../PortalButton/PortalButton';
// import MUI component
import { Drawer, Grid, Typography, ListItemIcon } from '@mui/material';
// icon
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// extract name for every service
import { serviceIdEnumMapKey, serviceIdEnumMapValue } from '../../../utils/serviceResolver';
import { formatMessage, checkEnrollmentStatusForService } from '../../utils/globalMethods';
import { routesList } from 'Utility/strings';
import { useIntl } from 'react-intl';
// this array could be passed by backand for a dynamic header
const headerLink = [
  {
    label: 'Program',
    translationLabel: formattedMessage.program,
    url: '',
    haveSecondLevel: false,
    secondLevelType: '',
    type: 'link',
  },
  {
    label: 'Our offer',
    translationLabel: formattedMessage.ourOffer,
    url: '/',
    haveSecondLevel: true,
    secondLevelType: 'packages',
    type: 'noLink',
  },
  {
    label: 'Services',
    translationLabel: formattedMessage.services,
    url: '/',
    haveSecondLevel: true,
    secondLevelType: 'services',
    type: 'noLink',
  },
  {
    label: 'Support',
    translationLabel: formattedMessage.support,
    url: 'support',
    haveSecondLevel: false,
    secondLevelType: '',
    type: 'link',
  },
  {
    label: 'Settings',
    translationLabel: formattedMessage.settings,
    url: 'profile',
    haveSecondLevel: false,
    secondLevelType: '',
    type: 'link',
  },
];

const PortalDrawer = (props) => {
  // enable navigation
  const navigate = useNavigate();
  // state for dropDown
  const intl = useIntl();
  const homepage = window.location.pathname === '/';
  const [openDropDown, setDropDownToOpen] = useState(-1);

  // function for navigation
  const navigation = (url, label, cardDetails, dictionaryId) => () => {
    // this action close the drawer
    props.handleOpenDrawerFunction();
    // this action popolate service or package details
    if (url === 'package-details') {
      navigate(routesList.packageDetailsRoutes.goToPackageDetails, {
        state: { packageDetails: cardDetails, dictionaryId: dictionaryId },
      });
    } else if (url === 'service/details') {
      // navigate at the url
      navigate(`/${url}`);
    } else {
      navigate(`/${url}`);
    }
    // this is for analytics
    props.actions.doAnalyticsMenuClick({
      url: url,
      label: label,
      secondLevel: cardDetails !== undefined,
      fromLevel: 'Our offer',
    });
  };

  // function that switch dropDown
  const dropDownOpen = (index) => () => {
    let newDropDownValue = index === openDropDown ? -1 : index;
    setDropDownToOpen(newDropDownValue);
  };
  // extract correct name for service
  const serviceIdHandler = (id) => {
    const serviceType = serviceIdEnumMapKey(id);
    const serviceName = { id: `t.app.global.${serviceType}.service.title` };
    return serviceName;
  };

  const goToServiceDetails = (id) => {
    props.actions.doAnalyticsMenuClick({
      url: routesList.serviceRoutes.goToServiceDetails,
      label: intl.formatMessage(serviceIdHandler(id)),
      secondLevel: true,
      fromLevel: 'Services',
    });
    if (
      props.subscribeStatus === 'T1' &&
      checkEnrollmentStatusForService(props.serviceList, id).status
    ) {
      navigate(routesList.serviceRoutes.goToServiceCatalogue, {
        state: { serviceType: serviceIdEnumMapKey(id) },
      });
    } else {
      navigate(routesList.serviceRoutes.goToServiceDetails, {
        state: { serviceType: serviceIdEnumMapKey(id) },
      });
    }

    props.handleOpenDrawerFunction();
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      dropDownOpen(index)();
    }
  };

  // render main header without dropdown (first level)
  const renderDrawerSection = (
    { translationLabel, url, haveSecondLevel, label, secondLevelType, type },
    index,
  ) => (
    <Grid key={index} className={style.drawerSection} container direction='column' tabIndex={-1}>
      <PortalAnchorButton
        endIcon={false}
        className={`headerLinkTitle ${props.locationPath === url ? 'focusLink' : ''} ${
          type === 'noLink' ? 'notInteractive' : ''
        }`}
        textTransform='isCapitalize'
        isTranslatedMessage
        id={label}
        contentText={translationLabel}
        onClickAction={
          type === 'noLink' ? null : haveSecondLevel ? dropDownOpen(index) : navigation(url, label)
        }
      />
      {haveSecondLevel && (
        <>
          <ListItemIcon
            className={style.dropDownArrow}
            onClick={dropDownOpen(index)}
            tabIndex='0'
            onKeyDown={handleKeyDown(index)}>
            <KeyboardArrowDownIcon
              className={`${style.dropDownIcon} ${openDropDown === index ? style.active : ''}`}
            />
          </ListItemIcon>
          <Grid
            className={`${style.sectionDropDownLinkContainer} ${
              openDropDown === index ? style.active : ''
            }`}
            container
            direction='column'>
            {renderDropdownLinks(secondLevelType)}
          </Grid>
        </>
      )}
    </Grid>
  );
  // render dropdown link (second level)
  const renderDropdownLinks = (secondLevelType) => {
    let check = '';
    let toEvaluate = '';
    let dropdownData = [];
    if (secondLevelType === 'packages') {
      check = 'id';
      toEvaluate = props.packageId;
      dropdownData = props.planList;
    } else if (secondLevelType === 'services') {
      check = 'idFormatEng';
      toEvaluate = serviceIdEnumMapValue(props.serviceId, 'key');
      dropdownData = props.serviceList;
    }
    // map the dropDown with some different things like path
    return dropdownData.map((element, index) => (
      <PortalAnchorButton
        endIcon={false}
        key={index}
        className={`headerLink
        ${
          secondLevelType === 'packages'
            ? toEvaluate === index + 1 && !homepage
              ? 'focusLink'
              : ''
            : toEvaluate === element[check] && !homepage
            ? 'focusLink'
            : ''
        }`}
        id={element.id}
        isTranslatedMessage={secondLevelType === 'packages' ? false : true}
        // textTransform={'isCapitalize'}
        contentText={
          secondLevelType === 'packages' ? element?.name : serviceIdHandler(element?.idFormatEng)
        }
        navigationObject={{
          externalNavigation: {
            openInNewTab: false,
            url: `${secondLevelType === 'packages' ? `package-details` : `service/details`}`,
          },
        }}
        onClickAction={
          secondLevelType === 'packages'
            ? navigation(`package-details`, element?.name, element, index + 1)
            : () => goToServiceDetails(element?.idFormatEng)
        }
      />
    ));
  };

  return (
    <Drawer anchor='top' open={props.drawerOpen} onClose={props.handleOpenDrawerFunction}>
      <Grid className={style.drawerContainer}>
        <Grid className={style.drawerNavigationContainer} container direction='row'>
          {props.multiplePosition && (
            <Grid className={style.positionSection}>
              <Typography variant={'body1'} className={style.currentPositionCaption}>
                {formatMessage(formattedMessage.currentPosition)}
              </Typography>
              <Typography className={style.currentPositionLabel}>
                {props.currentPositionLabel}{' '}
                <span className={style.currentPositionValue}>{props.currentPositionValue}</span>
              </Typography>
              <PortalButton
                variant={'outlined'}
                className={style.switchPositionButton}
                isTranslatedMessage
                onClick={props.switchPositionFunc}
                contentText={{
                  id: 't.app.header.switchPosition.button',
                  defaultMessage: 'SWITCH POSITION',
                }}
              />
            </Grid>
          )}
          {headerLink.map(renderDrawerSection)}
        </Grid>
        {props.hasButton ? (
          <Grid className={`${main.container} ${style.logoutSection}`}>
            <PortalAnchorButton
              className={style.exitButton}
              componentSize='large'
              color={'primary'}
              textTransform='isUppercase'
              isTranslatedMessage={true}
              contentText={formattedMessage.logout}
              navigationObject={{
                internalNavigation: {
                  openInNewTab: false,
                  url: 'logout',
                  onClickActionInternalNavigation: () => props.handleOpenDrawerFunction(),
                },
              }}
            />
          </Grid>
        ) : (
          ''
        )}
      </Grid>
    </Drawer>
  );
};

PortalDrawer.propTypes = {
  // Add here some propTypes
  // and set a minimal description i.e.
  /** Description of prop "baz". */
  drawerOpen: PropTypes.bool.isRequired,
  handleOpenDrawerFunction: PropTypes.func.isRequired,
  planList: PropTypes.array,
  serviceList: PropTypes.array,
  currentPositionLabel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentPositionValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  multiplePosition: PropTypes.bool,
  switchPositionFunc: PropTypes.func,
  locationPath: PropTypes.string,
  locationHeaderDetails: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  actions: PropTypes.objectOf(PropTypes.func),
  packageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  serviceId: PropTypes.string,
  subscribeStatus: PropTypes.string,
  hasButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

PortalDrawer.defaultProps = {
  // Add here some default propTypes values
  actions: {},
  drawerOpen: false,
  planList: [],
  serviceList: [],
  locationHeaderDetails: '',
  packageId: '',
  serviceId: '',
  subscribeStatus: '',
  hasButton: false,
};

export default PortalDrawer;
